import React, { useRef } from 'react';
import { ScrollView, View, Pressable } from 'react-native';
import useMergedRef from '@react-hook/merged-ref';
import { BodyText } from '../Text';
import { usePressableState } from '../../hooks';
import { styles } from './Select.styles';
import type { OptionProps, OptionsProps } from './Select.types';
import { focusOnElement } from '../../utils/accessibility/focus/focusOnElement.web';
import { webOnlyStyles } from '../../utils';

export const Options = React.forwardRef<View, OptionsProps>((props, ref) => {
  const { opened, selected, options, style, nativeID } = props;
  const { onLayout, onSelect } = props;
  const firstElement = React.useRef(null);

  React.useEffect(() => {
    if (!opened) return;
    focusOnElement(firstElement);
  }, [firstElement, opened]);

  if (!opened) return null;

  return (
    <View
      nativeID={nativeID}
      ref={ref}
      style={[styles.options, style]}
      onLayout={onLayout}
      accessible={false}
      focusable={false}
    >
      <ScrollView
        testID="sg-select-options"
        accessible={false}
        focusable={false}
        style={styles.scrollView}
      >
        {options.map((option, index) => (
          <Option
            ref={index === 0 ? firstElement : null}
            key={option.value}
            isSelected={option.value === selected}
            option={option}
            onSelect={onSelect}
          />
        ))}
      </ScrollView>
    </View>
  );
});

const Option = React.forwardRef<View, OptionProps>((props, ref) => {
  const { isSelected, option, onSelect } = props;
  const innerRef = useRef<View>(null);
  const multiRef = useMergedRef(ref, innerRef);
  const { isActive, isHovered } = usePressableState(innerRef);
  const selectOption = React.useCallback(() => {
    onSelect(option.value);
  }, [option, onSelect]);
  const hoveredStyle = isHovered ? styles.hovered : null;
  const activeStyle = isActive ? styles.pressed : null;
  const selectedStyle = isSelected ? styles.selected : null;
  const disabledStyle = option.disabled
    ? [
        styles.optionDisabled,
        webOnlyStyles({ userSelect: 'none', cursor: 'not-allowed' }),
      ]
    : null;
  const style = [
    styles.option,
    hoveredStyle,
    activeStyle,
    selectedStyle,
    disabledStyle,
  ];
  const labelStyle = option.disabled ? styles.disabled : null;

  return (
    <Pressable
      testID={`sg-select-option-${option.value}`}
      accessibilityRole="button"
      accessibilityLabel={`select option for ${option.label}`}
      ref={multiRef}
      style={style}
      disabled={option.disabled}
      onPress={selectOption}
    >
      <BodyText style={labelStyle} size={4}>
        {option.label}
      </BodyText>
    </Pressable>
  );
});
